import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import strftime from "strftime";

import HaspObject from './HaspObject';
import { splitColorOpa } from '../utils/color';
import { escapeCString, parseUnicodeNotation } from '../utils/hasputils';
import { processIconText } from '../utils/iconFonts';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { resolvePadding, contentBox, onPaddingStyle } from './properties/v2/padding.js';

export const LABEL_MODE_EXPAND = 'expand'; //Expand the object size to the text size
export const LABEL_MODE_BREAK = 'break'; //Keep the object width, break the too long lines and expand the object height
export const LABEL_MODE_DOTS = 'dots'; //Keep the size and write dots at the end if the text is too long
export const LABEL_MODE_SCROLL = 'scroll'; //Keep the size and roll the text back and forth
export const LABEL_MODE_LOOP = 'loop'; //loop Keep the size and roll the text circularly
export const LABEL_MODE_CROP = 'crop'; //crop Keep the size and crop the text out of it

export const LABEL_ALIGN_LEFT = 'left';
export const LABEL_ALIGN_CENTER = 'center';
export const LABEL_ALIGN_RIGHT = 'right';

export const LABEL_ALIGN_OPTIONS = [
  { value: LABEL_ALIGN_LEFT, description: 'Left' },
  { value: LABEL_ALIGN_CENTER, description: 'center' },
  { value: LABEL_ALIGN_RIGHT, description: 'right' },
];

export const LABEL_MODE_OPTIONS = [
  { value: LABEL_MODE_EXPAND, description: 'Expand object size' },
  { value: LABEL_MODE_BREAK, description: 'Break long lines' },
  { value: LABEL_MODE_DOTS, description: 'Dots ...' },
  { value: LABEL_MODE_SCROLL, description: 'Scroll, roll text' },
  { value: LABEL_MODE_LOOP, description: 'Loop, roll circular' },
  { value: LABEL_MODE_CROP, description: 'Crop long lines' },
];

export const LABEL_DECOR_OPTIONS = [
  { value: 0, description: "none" },
  { value: 1, description: "underline" },
  { value: 2, description: "strikethrough" },
  { value: 3, description: "underline and strikethrough" },
];

export default class HaspLabel extends HaspObject {
  hasp_enabled = true;
  hasp_mode_options = LABEL_MODE_OPTIONS;
  hasp_align_options = LABEL_ALIGN_OPTIONS;
  hasp_text_decor_options = LABEL_DECOR_OPTIONS;
  intervalId = null; // Store the timer reference

  constructor(config) {
    super(config);

    const { text_color } = this.style('main');
    // Firmware insets a label's text by its padding (lv_label.c:1527-1534), so
    // the text node lives in the content box, not the widget box — see
    // _applyTextBox() below. Padding defaults to 0 and no bundled theme sets
    // it, so an existing design renders byte-identically.
    const initialBox = contentBox(this.style('main'), this.width(), this.height());
    this.textObj = new Konva.Text({
      x: initialBox.x,
      y: initialBox.y,
      width: 'auto',
      height: 'auto',
      verticalAlign: 'middle',
      text: processIconText(this.text),
      align: this.align,
      fill: text_color,
      listening: false,
      draggable: false,
      strokeWidth: 0,
      obj: 'text',
    });

    this.add(this.textObj);
    this._fontBinding = new FontBinding((family) => {
      this.textObj.fontFamily(family);
      this.textObj.getLayer()?.batchDraw();
    });
    this._applyTextFont();
    this.draggable(true);
    this.setTextMode();
    this.idTextObj.moveToTop();


    this.on('transform', function () {
      if (this.scaleX() === 1) {
        this._applyTextBox();
      }
    });

    this.on('transformend', (e) => {
      // console.log("transformEnd event in HaspLabel")
      this._applyTextBox();
      this.size(this.rect.size());
    });

    this.on('text_align', (e) => {
      this.textObj.align(this.align);
      this.setTextMode();
    });

    this.on('hasp_mode', (e) => {
      this.setTextMode();
    });

    this.onStyle('main', 'text_color', () => {
      this.textObj.fill(this.style('main').text_color);
    });

    this.onStyle('main', 'text_font', () => {
      this._applyTextFont();
      this._sizeToText();
      this.setTextMode();
    });

    this.on('font', (e) => {
      this._applyTextFont();
      this._sizeToText();
      this.setTextMode();
    });

    this.onStyle('main', 'text_font_family', () => {
      // A loaded font resource owns fontFamily while active — FontBinding
      // itself skips the callback in that case, so this can't clobber it.
      this._applyTextFont();
      // Force redraw to show the new font (especially for icon fonts)
      this.setText();
      if (this.textObj.getLayer()) {
        this.textObj.getLayer().requestRender();
      }
    });

    this.on('destroy', () => {
      this._fontBinding.dispose();
    });

    this.on('text_text', (e) => {
      this.setText();
      this.setTextMode();
      this._sizeToText();
      // this.rect.width(this.width());
      // this.rect.height(this.height());
    });
    this.on('template_text', (e) => {
      this.setText();
    });

    this.onStyle('main', 'text_letter_space', () => {
      this.setLetterSpacing(Number(this.style('main').text_letter_space));
    });

    this.onStyle('main', 'text_decor', () => {
      this.setTextDecor(this.style('main').text_decor);
    });

    // Shorthand + all four per-side overrides, one subscription helper.
    onPaddingStyle(this, 'main', () => {
      this._applyTextBox();
      this.setTextMode();
    });

  }

  /**
   * Move/resize the text node to the effective content box — the single place
   * this widget turns padding into text geometry (per-side values resolved by
   * v2/padding.js, never re-derived here).
   */
  _applyTextBox() {
    const box = contentBox(this.style('main'), this.rect.width(), this.rect.height());
    this.textObj.x(box.x);
    this.textObj.y(box.y);
    this.textObj.size({ width: box.width, height: box.height });
  }

  /** Auto-size the widget to its text PLUS the effective padding. */
  _sizeToText() {
    const { left, right, top, bottom } = resolvePadding(this.style('main'));
    const size = this.textObj.size();
    this.rect.size({ width: size.width + left + right, height: size.height + top + bottom });
    this._applyTextBox();
  }

  startUpdating() {
    if (this.intervalId) clearInterval(this.intervalId); // Prevent multiple timers
    this.intervalId = setInterval(() => this.setText(), 1000); // Update every second
  }

  setNewSize(size) {
    super.setNewSize(size);
    this._applyTextBox();
  }

  stopUpdating() {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = null;
  }

  /**
   * The uploaded Font Resource `this.font` resolves to for canvas preview,
   * or null if it doesn't match one (empty or a legacy firmware value).
   */
  _fontResource() {
    return this.font ? useFontStore().fonts.find(f => f.file === this.font) : null;
  }

  /**
   * Resolve which font to render with. `font` holds the actual wire-format
   * Font Family string (a project Font Resource's on-device `file` path — see
   * label.js's `font` property comment) — not a Designer uuid. Canvas preview
   * only has real bytes to render for a project font, so this just checks
   * whether any uploaded Font Resource's `file` happens to match; an empty
   * value uses the bundled browser-only Roboto Condensed fallback.
   */
  _applyTextFont() {
    const { text_font, text_font_family } = this.style('main');
    this.textObj.fontSize(text_font);
    this._fontBinding.set(this._fontResource(), text_font_family || 'Roboto Condensed');
  }

  setText() {
    if (!this.textObj) return;

    if (this.template) {
      const templateText = strftime(this.template, new Date());
      // Process icons in template text too (supports \uXXXX notation)
      this.textObj.text(processIconText(templateText));
      if (this.intervalId === null)
        this.startUpdating();
    } else {
      // Process text to convert icon placeholders and Unicode escapes
      const processedText = processIconText(this.text);
      this.textObj.text(processedText);
      if (this.intervalId !== null)
        this.stopUpdating();
    }

    this.textObj.getLayer().requestRender(); // Redraw efficiently
  }

  setTextLabel(text) {
    //ToDo: calculate text size based on mode
    this.text = text;
    //set the text
    this.setTextMode();
    // Process icons when setting text directly
    this.textObj.text(processIconText(this.text));
  }

  setTextMode() {
    // Every width/height below is the CONTENT width/height — the widget box
    // minus its effective per-side padding — and every size the widget takes
    // FROM the text adds that padding back. With padding 0 (the default) these
    // are the same expressions as before.
    const { left, right, top, bottom } = resolvePadding(this.style('main'));
    const padX = left + right;
    const padY = top + bottom;
    const contentW = () => Math.max(0, this.width() - padX);
    const contentH = () => Math.max(0, this.height() - padY);
    this.textObj.x(left);
    this.textObj.y(top);

    //if not dots disable elipsis
    if (this.mode !== LABEL_MODE_DOTS) this.textObj.ellipsis(false);
    //if not break diable wrap
    if (this.mode !== LABEL_MODE_BREAK) this.textObj.wrap('none');
    if (this.mode !== LABEL_MODE_EXPAND) {
      this.textObj.width(contentW());
    }

    if (this.mode === LABEL_MODE_BREAK) {
      this.textObj.width(Math.max(0, this.rect.width() - padX));
      this.textObj.wrap('word');
      this.height(this.textObj.height() + padY);
    } else if (this.mode === LABEL_MODE_DOTS) {
      this.textObj.ellipsis(true);
    } else if (
      this.mode === LABEL_MODE_CROP ||
      this.mode === LABEL_MODE_LOOP ||
      this.mode === LABEL_MODE_SCROLL
    ) {
      // adjust size to text
      this.textObj.width(contentW());
    } else if (this.mode === LABEL_MODE_EXPAND) {
      this.textObj.width('auto');
      if (this.textObj.width() > contentW()) {
        this.width(this.textObj.width() + padX);
        this.rect.width(this.textObj.width() + padX);
      } else {
        this.textObj.width(contentW());
      }
      this.textObj.height('auto');
      if (this.textObj.height() > contentH()) {
        this.height(this.textObj.height() + padY);
      } else {
        this.textObj.height(contentH());
      }
    }
  }

  setLetterSpacing(spacing) {
    this.textObj.letterSpacing(Number(spacing));
  }

  setTextDecor(textDecor) {
    // 0 = none (=default)
    // 1 = underline
    // 2 = strikethrough
    // 3 = underline and strikethrough
    // Konva: 'underline', 'line-through'
    let decor = '';
    switch (textDecor) {
      case 1: decor = 'underline';
        break;
      case 2:
        decor = 'line-through';
        break;
      case 3: decor = 'underline line-through';
        break;
    }
    this.textObj.textDecoration(decor);
  }

}
